import React, { useState } from 'react'
import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { useTheme } from '@/hooks/useTheme'

interface ConfirmAvatarProps {
  size: number
  uri?: string
  name?: string
  iconSize?: number
  gradientColors?: [string, string, ...string[]]
  userIsFavorite?: boolean
}

export const ConfirmAvatar: React.FC<ConfirmAvatarProps> = ({
  size,
  uri,
  name,
  iconSize = 24,
  gradientColors,
  userIsFavorite = false,
}) => {
  const theme = useTheme()
  const [imageError, setImageError] = useState(false)

  // Generate initials from name
  const getInitials = (fullName?: string) => {
    if (!fullName) return '?'

    const words = fullName.trim().split(' ')
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase()
    }

    return (
      words[0].charAt(0) + words[words.length - 1].charAt(0)
    ).toUpperCase()
  }

  // Create gradient or use default color
  const getBackgroundStyle = () => {
    if (gradientColors && gradientColors.length >= 2) {
      return {
        background: `linear-gradient(135deg, ${gradientColors.join(', ')})`,
      }
    }

    // Default color - nicer gradient
    return {
      background: `linear-gradient(135deg, ${theme.colors.background.secondaryGray || '#E5E5E7'}, ${theme.colors.background.cardItemRoot || '#F8F9FA'})`,
    }
  }

  const initials = getInitials(name)
  const shouldShowImage = uri && !imageError

  // Dimensions for pinned badge (as in RN project)
  const pinnedFlagSize = 26
  const pinnedContainerSize = pinnedFlagSize + 10

  return (
    <BaseView
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${size / 2}px`,
        overflow: 'visible', // Changed to display badge
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...getBackgroundStyle(),
        // Add shadow for aesthetics
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      {shouldShowImage && (
        <img
          src={uri}
          alt={name || 'Avatar'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: `${size / 2}px`,
          }}
          onError={() => setImageError(true)}
          onLoad={() => setImageError(false)}
        />
      )}

      {/* Fallback with initials - show if no image or error */}
      {!shouldShowImage && (
        <BaseText
          fontSize={iconSize}
          fontFamily="semibold"
          color="label.default"
          style={{
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          {initials}
        </BaseText>
      )}

      {/* Pinned Badge - as in RN project */}
      {userIsFavorite && (
        <BaseView
          style={{
            width: `${pinnedContainerSize}px`,
            height: `${pinnedContainerSize}px`,
            borderRadius: `${pinnedContainerSize / 2}px`,
            backgroundColor: theme.colors.background.screen || '#111827',
            position: 'absolute',
            right: '-0px',
            bottom: '-2px',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BaseView
            style={{
              width: `${pinnedFlagSize}px`,
              height: `${pinnedFlagSize}px`,
              borderRadius: `${pinnedFlagSize / 2}px`,
              background: `linear-gradient(135deg, ${
                theme.colors.background.buttonPrimaryGradient1 || '#8B5CF6'
              }, ${theme.colors.background.buttonPrimaryGradient2 || '#A855F7'})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <AppIcons name="pin" size={22} color="white" />
          </BaseView>
        </BaseView>
      )}
    </BaseView>
  )
}

export default ConfirmAvatar
